import { useQuery } from "@apollo/client";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  multiSelect,
  SelectList,
  Table,
} from "../../../../components/Functional";
import Header from "../../../../components/ui/ListHeader";
import { LIST_TYPES } from "../../../../constants/listTypes";
import { FETCH_LISTS as fetchLists } from "../../../../graphql/query/List";
import { updateSearchResultNavigationStore } from "../../../../util/searchResultNavigation";
import { warehouseNavigationStore } from "../../components/useWarehouseFilter";
import Actions from "./components/Actions";
import searchedWarehouses from "./components/SearchedWarehouses";
import * as SelectListSaved from "./components/SelectListSaved";
import { getWarehouseItemDetailsUrl } from "../../warehouseItemNavigation";
import * as SelectListWorking from "./components/SelectListWorking";

const List = props => {
  const { formik } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const { data: { lists: { edges = [] } = {} } = {} } = useQuery(fetchLists(), {
    variables: {
      input: { typeId: LIST_TYPES.WAREHOUSE_ITEM_WORKING },
    },
    fetchPolicy: "network-only",
  });

  const workingList = edges?.length > 0 ? edges[0] : null;
  const workingListId = workingList?.id;
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams?.get("status") || "all";
  const listId = +queryParams.get("listId");
  const ids = Object.keys(formik?.values?.selected?.ids || {}).map(item =>
    parseInt(item.replace("_", "")),
  );

  const multiSelectProps = {
    formik,
    label: "Warehouses",
    name: "selected",
    keyName: "id",
  };

  const targetListId =
    listId && !isNaN(listId) ? listId : workingListId || undefined;
  const actionProps = {
    formik,
    ids,
    listId,
    workingListId,
    onSuccess: () => formik?.resetForm(),
  };

  const captureNavigationSnapshot = useCallback(
    ({ count, edges, queryVariables }) => {
      if (status !== "all") return;

      updateSearchResultNavigationStore(warehouseNavigationStore, {
        count,
        edges,
        listContext: { locationSearch: location.search },
        queryVariables,
        statusContext: status,
      });
    },
    [location.search, status],
  );

  const handleChange = value => {
    formik?.resetForm();
    let url = `/warehouse?status=${value}`;

    // For working list tabs, use listId if present (saved list), otherwise use workingListId
    if (value === "selectListWorking" || value === "selectListSaved") {
      // Include listId if present (viewing/editing a saved list), otherwise use workingListId
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
    } else {
      // For "all" and other tabs, preserve saved list IDs (listId) so items can be added to them
      // But don't include working list IDs
      if (listId) {
        url += `&listId=${listId}`;
      }
    }

    navigate(url);
  };

  const GeneralTabActions = () => {
    const createListProps = {
      ...actionProps,
      // Use listId if present (saved list), otherwise use workingListId
      ...(targetListId ? { targetListId } : {}),
      onSuccess: () => {
        formik?.resetForm();
        navigate(
          `/warehouse?status=selectListWorking${listId ? `&listId=${listId}` : ""}`,
        );
      },
      typeId: LIST_TYPES.WAREHOUSE_ITEM_WORKING,
    };

    return <SelectList.CreateList {...createListProps} />;
  };

  const tabProps = {
    active: status,
    onChange: handleChange,
    tabs: [
      {
        actions: GeneralTabActions,
        label: "All Warehouses",
        name: "all",
        tableProps: searchedWarehouses(),
      },
      {
        actions: SelectListWorking.Actions,
        label: "Select List (Working)",
        name: "selectListWorking",
        tableProps: SelectListWorking.tableProps(actionProps),
      },
      {
        actions: SelectListSaved.Actions,
        label: "Select List (Saved)",
        name: "selectListSaved",
        tableProps: SelectListSaved.tableProps(actionProps),
      },
    ],
  };

  const { tableProps: tabTableProps, actions: tabActions } =
    tabProps?.tabs?.find(tab => tab.name === status) || {};

  // function fixes header.name for select lists when status = selectListWorking
  const fixHeadersName = ({ status, headers }) =>
    headers?.map(header => {
      return {
        ...header,
        name:
          status === "selectListWorking"
            ? `item.${header?.name}`
            : header?.name,
      };
    });

  const tableProps = {
    headers: fixHeadersName({
      status,
      headers: [
        multiSelect(multiSelectProps),
        {
          label: "Id",
          name: "id",
          sortKey: "id",
          type: "link",
          to: props => {
            const { values } = props;
            return getWarehouseItemDetailsUrl(values, window.location.search);
          },
        },
        {
          label: "Category",
          name: "category.description",
          type: "label",
        },
        {
          label: "Description",
          name: "description",
          type: "label",
        },
        {
          label: "Destroyed",
          name: "destroyed",
          type: "input",
          inputType: "toggle",
          designType: "threeStateCheckbox",
        },
        {
          label: "Weight Type",
          name: "weightType.name",
          type: "label",
        },
        {
          label: "Weight",
          name: "weight",
          sortKey: "weight",
          type: "label",
        },
        { label: "", name: "action", type: "custom", component: Actions },
      ],
    }),
    tabProps,
    variant: "dynamicHeight",
    ...tabTableProps,
    onQueryStateChange: captureNavigationSnapshot,
  };

  return (
    <>
      <Header
        leftText="All Warehouse"
        rightComponent={() => {
          return <div className="flex gap-4">{tabActions?.(actionProps)}</div>;
        }}
      />
      <div className="mt-6 w-full">
        <Table {...tableProps} />
      </div>
    </>
  );
};

export default List;
